import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Plus, Trash2, Edit, Save, X, Loader2, FileQuestion, 
  Clock, Award, Target, AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ManageTests = () => {
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTest, setEditingTest] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    course_category: 'SSC',
    subject: '',
    duration_minutes: 60,
    passing_marks: 0,
    questions: []
  });

  useEffect(() => {
    fetchTests();
  }, []);

  const fetchTests = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/tests/admin/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setTests(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching tests:', error);
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setFormData({
      title: '',
      description: '',
      course_category: 'SSC',
      subject: '',
      duration_minutes: 60,
      passing_marks: 0,
      questions: []
    });
    setEditingTest(null);
    setShowForm(true);
  };

  const handleAddQuestion = () => {
    const newQuestion = {
      question_text: '',
      options: ['', '', '', ''],
      correct_answer: 0,
      marks: 1,
      negative_marks: 0.25,
      explanation: ''
    };
    setFormData({
      ...formData,
      questions: [...formData.questions, newQuestion]
    });
  };

  const handleRemoveQuestion = (index) => {
    const updated = formData.questions.filter((_, i) => i !== index);
    setFormData({ ...formData, questions: updated });
  };

  const handleQuestionChange = (qIndex, field, value) => {
    const updated = [...formData.questions];
    if (field === 'options') {
      // value is {optionIndex, text}
      updated[qIndex].options[value.optionIndex] = value.text;
    } else {
      updated[qIndex][field] = value;
    }
    setFormData({ ...formData, questions: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.title || !formData.subject || formData.questions.length === 0) {
      alert('Please fill all required fields and add at least one question');
      return;
    }

    // Check all questions are complete
    for (let i = 0; i < formData.questions.length; i++) {
      const q = formData.questions[i];
      if (!q.question_text || q.options.some(opt => !opt)) {
        alert(`Question ${i + 1} is incomplete. Please fill all fields.`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/tests/admin`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        alert('Test created successfully!');
        setShowForm(false);
        fetchTests();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error creating test:', error);
      alert('Failed to create test');
    }
    setSubmitting(false);
  };

  const handleDelete = async (testId) => {
    if (!window.confirm('Are you sure you want to delete this test?')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/tests/admin/${testId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('Test deleted successfully');
        fetchTests();
      }
    } catch (error) {
      console.error('Error deleting test:', error);
      alert('Failed to delete test');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="animate-spin text-red-600" size={48} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <FileQuestion className="mr-3 text-red-600" size={32} />
              Manage Tests
            </h1>
            <p className="text-gray-600 mt-1">Create and manage mock tests for students</p>
          </div>
          <Button
            onClick={handleCreateNew}
            className="bg-red-600 hover:bg-red-700"
          >
            <Plus className="mr-2" size={16} />
            Create New Test
          </Button>
        </div>

        {/* Create/Edit Form */}
        {showForm && (
          <Card className="border-2 border-red-200">
            <CardHeader className="bg-red-50">
              <CardTitle>
                {editingTest ? 'Edit Test' : 'Create New Test'}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Info */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="title">Test Title *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="e.g., SSC CGL Mock Test 1"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="subject">Subject *</Label>
                    <Input
                      id="subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      placeholder="e.g., Quantitative Aptitude"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="description">Description *</Label>
                  <textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of the test"
                    className="w-full min-h-[80px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    required
                  />
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="category">Course Category *</Label>
                    <select
                      id="category"
                      value={formData.course_category}
                      onChange={(e) => setFormData({ ...formData, course_category: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                      required
                    >
                      <option value="SSC">SSC</option>
                      <option value="Banking">Banking</option>
                      <option value="RBI">RBI Grade B</option>
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="duration">Duration (minutes) *</Label>
                    <Input
                      id="duration"
                      type="number"
                      value={formData.duration_minutes}
                      onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
                      min="1"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="passing">Passing Marks *</Label>
                    <Input
                      id="passing"
                      type="number"
                      value={formData.passing_marks}
                      onChange={(e) => setFormData({ ...formData, passing_marks: parseInt(e.target.value) })}
                      min="0"
                      required
                    />
                  </div>
                </div>

                {/* Questions Section */}
                <div className="border-t pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold">Questions</h3>
                    <Button
                      type="button"
                      onClick={handleAddQuestion}
                      variant="outline"
                      size="sm"
                    >
                      <Plus className="mr-2" size={16} />
                      Add Question
                    </Button>
                  </div>

                  {formData.questions.length === 0 ? (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <AlertCircle className="mx-auto text-gray-400 mb-2" size={48} />
                      <p className="text-gray-600">No questions added yet. Click "Add Question" to start.</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {formData.questions.map((question, qIndex) => (
                        <Card key={qIndex} className="border-l-4 border-l-blue-500">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between mb-3">
                              <h4 className="font-semibold text-gray-900">Question {qIndex + 1}</h4>
                              <Button
                                type="button"
                                onClick={() => handleRemoveQuestion(qIndex)}
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 size={16} />
                              </Button>
                            </div>

                            {/* Question Text */}
                            <div className="mb-4">
                              <Label>Question Text *</Label>
                              <textarea
                                value={question.question_text}
                                onChange={(e) => handleQuestionChange(qIndex, 'question_text', e.target.value)}
                                placeholder="Enter the question"
                                className="w-full min-h-[80px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                required
                              />
                            </div>

                            {/* Options */}
                            <div className="grid md:grid-cols-2 gap-3 mb-4">
                              {question.options.map((option, optIndex) => (
                                <div key={optIndex}>
                                  <Label>Option {String.fromCharCode(65 + optIndex)}</Label>
                                  <Input
                                    value={option}
                                    onChange={(e) => handleQuestionChange(qIndex, 'options', { optionIndex: optIndex, text: e.target.value })}
                                    placeholder={`Option ${String.fromCharCode(65 + optIndex)}`}
                                    required
                                  />
                                </div>
                              ))}
                            </div>

                            {/* Marks & Correct Answer */}
                            <div className="grid md:grid-cols-3 gap-3">
                              <div>
                                <Label>Correct Answer *</Label>
                                <select
                                  value={question.correct_answer}
                                  onChange={(e) => handleQuestionChange(qIndex, 'correct_answer', parseInt(e.target.value))}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                  required
                                >
                                  <option value={0}>Option A</option>
                                  <option value={1}>Option B</option>
                                  <option value={2}>Option C</option>
                                  <option value={3}>Option D</option>
                                </select>
                              </div>

                              <div>
                                <Label>Marks *</Label>
                                <Input
                                  type="number"
                                  value={question.marks}
                                  onChange={(e) => handleQuestionChange(qIndex, 'marks', parseInt(e.target.value))}
                                  min="1"
                                  required
                                />
                              </div>

                              <div>
                                <Label>Negative Marks</Label>
                                <Input
                                  type="number"
                                  step="0.25"
                                  value={question.negative_marks}
                                  onChange={(e) => handleQuestionChange(qIndex, 'negative_marks', parseFloat(e.target.value))}
                                  min="0"
                                />
                              </div>
                            </div>

                            {/* Explanation (Optional) */}
                            <div className="mt-4">
                              <Label>Explanation (Optional)</Label>
                              <textarea
                                value={question.explanation || ''}
                                onChange={(e) => handleQuestionChange(qIndex, 'explanation', e.target.value)}
                                placeholder="Provide a detailed explanation for the correct answer (recommended for better learning)"
                                className="w-full min-h-[80px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                              />
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>

                {/* Form Actions */}
                <div className="flex gap-3 pt-4 border-t">
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 animate-spin" size={16} />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2" size={16} />
                        {editingTest ? 'Update Test' : 'Create Test'}
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setShowForm(false)}
                    variant="outline"
                  >
                    <X className="mr-2" size={16} />
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Tests List */}
        <Card>
          <CardHeader>
            <CardTitle>All Tests ({tests.length})</CardTitle>
            <CardDescription>Manage your test series and mock exams</CardDescription>
          </CardHeader>
          <CardContent>
            {tests.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <FileQuestion className="mx-auto text-gray-400 mb-3" size={48} />
                <p className="text-gray-600 mb-4">No tests created yet</p>
                <Button onClick={handleCreateNew} className="bg-red-600 hover:bg-red-700">
                  <Plus className="mr-2" size={16} />
                  Create Your First Test
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {tests.map((test) => (
                  <Card key={test.id} className="border hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-lg font-bold text-gray-900">{test.title}</h3>
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              test.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {test.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          
                          <p className="text-sm text-gray-600 mb-3">{test.description}</p>
                          
                          <div className="flex flex-wrap gap-4 text-sm text-gray-700">
                            <span className="flex items-center">
                              <Award className="mr-1 text-red-600" size={14} />
                              <strong className="mr-1">Category:</strong> {test.course_category}
                            </span>
                            <span className="flex items-center">
                              <FileQuestion className="mr-1 text-purple-600" size={14} />
                              <strong className="mr-1">Subject:</strong> {test.subject}
                            </span>
                            <span className="flex items-center">
                              <Clock className="mr-1 text-orange-600" size={14} />
                              <strong className="mr-1">Duration:</strong> {test.duration_minutes} mins
                            </span>
                            <span className="flex items-center">
                              <Target className="mr-1 text-green-600" size={14} />
                              <strong className="mr-1">Questions:</strong> {test.question_count}
                            </span>
                            <span className="flex items-center">
                              <strong className="mr-1">Total Marks:</strong> {test.total_marks}
                            </span>
                            <span className="flex items-center">
                              <strong className="mr-1">Passing:</strong> {test.passing_marks}
                            </span>
                          </div>
                          
                          <p className="text-xs text-gray-500 mt-2">
                            Created by: {test.created_by} | {new Date(test.created_at).toLocaleDateString('en-IN')}
                          </p>
                        </div>

                        <div className="flex gap-2 ml-4">
                          <Button
                            onClick={() => handleDelete(test.id)}
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default ManageTests;
